/**
 * Clinician login (§7). Branded chrome — this is not an exercise screen.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api.js';
import styles from './ClinicianLogin.module.css';

export function ClinicianLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /**
   * Off by default. This screen can be sitting on a large monitor in an
   * occupied room, so revealing the password has to be a deliberate act rather
   * than the state you find it in.
   */
  const [showPassword, setShowPassword] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post('/auth/login', { username, password, context: 'DASHBOARD' });
      navigate('/clinician', { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.status === 401
          ? 'Incorrect username or password.'
          : 'Could not sign in. Please try again.',
      );
      setSubmitting(false);
    }
  }

  return (
    <main className={styles.page}>
      <form className={styles.card} onSubmit={onSubmit}>
        <img
          src="/brand/logo-small.png"
          alt="Pathways Brain &amp; Spinal Care"
          className={styles.logo}
          width={400}
          height={150}
        />

        <h1 className={styles.heading}>Clinician sign in</h1>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="username">
            Username
          </label>
          <input
            id="username"
            className={styles.input}
            type="text"
            name="username"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            autoFocus
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="password">
            Password
          </label>
          <div className={styles.passwordRow}>
            <input
              id="password"
              className={`${styles.input} ${styles.passwordInput}`}
              type={showPassword ? 'text' : 'password'}
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className={styles.reveal}
              onClick={() => setShowPassword((shown) => !shown)}
              aria-controls="password"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              title={showPassword ? 'Hide password' : 'Show password'}
            >
              <EyeIcon crossedOut={showPassword} />
            </button>
          </div>
        </div>

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        <button className={styles.submit} type="submit" disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>

        <p className={styles.footnote}>
          Patients do not sign in here. Patient access uses a therapy code on a
          separate screen.
        </p>
      </form>
    </main>
  );
}

/**
 * Inline rather than an icon dependency — one shape, two states (constraint 8).
 * Sized in em and drawn in currentColor, so it follows the button's own type
 * size and colour and defines neither itself.
 */
function EyeIcon({ crossedOut }: { crossedOut: boolean }) {
  return (
    <svg
      className={styles.eye}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
      {crossedOut && <line x1="3" y1="21" x2="21" y2="3" />}
    </svg>
  );
}
